import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardDataContext } from '../contexts/DashboardDataContext';
import { db } from '../firebase';
import { Case, Counselor, User, UserRole } from '../types';
import { logCaseAssigned, logCaseProposed, logCaseCreated } from '../utils/activityLogger';
import { requestAccountCredentialsEmail } from '../utils/appEmails';
import { t } from '../utils/translations';
import { mapFirestoreCase, shouldAppearInPersonalCases } from '../components/Cases/casesUtils';
import type { ReassignAssignmentFields } from '../components/Cases/ReassignCaseDialog';
import {
  CreateUserData,
  AdminTab,
  CaseStatusFilter,
  WorkloadFilter,
  parseAdminTabFromUrl,
  adminTabToSearchParam,
  filterCounselors,
  filterAdminCases,
  countAdminCasesByStatus,
  enrichCounselorsList,
  isValidEmail,
  emptyCreateUserData,
  filterUsersByWorkload,
  countUsersByWorkload,
  findCounselorForUser,
} from '../components/Admin/adminUtils';
import { countByWorkload, dedupeCounselors, mapFirestoreCounselor } from '../components/Counselors/counselorsUtils';
import { syncLinkedUserAvatar } from '../utils/avatarUtils';
import { assertUserHasRole } from '../utils/roleAuth';
import {
  splitDisplayName,
  toE164RoPhone,
  toRoPhoneDigitsInput,
  toDateInputValue,
  parseDateInputValue,
} from '../utils/nameUtils';

export function useAdminData() {
  const {
    currentUser,
    createUser,
    updateUserRole,
    deleteUser,
    deactivateUser,
    reactivateUser,
    getAllUsers,
    transferSupremeLeader,
  } = useAuth();
  const {
    upsertCase,
    removeCase,
    counselorRecordId,
    clearAssignmentNotificationsForCase,
    resolveAssignmentOutcomesForCase,
  } = useDashboardDataContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<AdminTab>(0);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [reactivatingUserId, setReactivatingUserId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [createUserData, setCreateUserData] = useState<CreateUserData>(emptyCreateUserData());
  const [editUserData, setEditUserData] = useState<Partial<CreateUserData>>({
    firstName: '',
    lastName: '',
    fullName: '',
    role: 'counselor',
  });
  const [newlyCreatedUserId, setNewlyCreatedUserId] = useState<string | null>(null);
  const [pendingProfileRequired, setPendingProfileRequired] = useState(false);
  const credentialsManuallyCopiedRef = useRef(false);

  /**
   * Counselors as stored — deduped, not yet enriched. Workload is derived below from `allCases`,
   * so this loader no longer reads the cases collection itself, which it used to do in full
   * right after `loadCases` had just done the same.
   */
  const [rawCounselors, setRawCounselors] = useState<Counselor[]>([]);
  const [counselorsLoading, setCounselorsLoading] = useState(true);
  const [counselorsError, setCounselorsError] = useState('');
  const [counselorFormOpen, setCounselorFormOpen] = useState(false);
  const [editingCounselor, setEditingCounselor] = useState<Counselor | null>(null);
  const [counselorSearchTerm, setCounselorSearchTerm] = useState('');
  const [counselorWorkloadFilter, setCounselorWorkloadFilter] = useState<WorkloadFilter>('all');
  const [userWorkloadFilter, setUserWorkloadFilter] = useState<WorkloadFilter>('all');

  const [allCases, setAllCases] = useState<Case[]>([]);
  const [casesLoading, setCasesLoading] = useState(true);
  const [casesError, setCasesError] = useState('');

  /** Workload folded in from the single cases list rather than from a second read. */
  const counselors = useMemo(
    () => enrichCounselorsList(rawCounselors, allCases),
    [rawCounselors, allCases]
  );

  // Flipped on a successful load. The tab effect skips loaders that already ran, so switching
  // tabs no longer re-reads whole collections; mutation handlers still refresh explicitly.
  const usersLoadedRef = useRef(false);
  const counselorsLoadedRef = useRef(false);
  const casesLoadedRef = useRef(false);
  const [caseSearchTerm, setCaseSearchTerm] = useState('');
  const [caseStatusFilter, setCaseStatusFilter] = useState<CaseStatusFilter>('all');
  const [caseCounselorFilter, setCaseCounselorFilter] = useState('all');
  const [caseFormOpen, setCaseFormOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<Case | null>(null);
  const [reassigningCase, setReassigningCase] = useState<Case | null>(null);
  const [sessionReportOpen, setSessionReportOpen] = useState(false);
  const [selectedCaseForSessionReport, setSelectedCaseForSessionReport] = useState<Case | null>(
    null
  );

  const isSupremeLeader = currentUser?.isSupremeLeader === true;
  const canManageUsers = currentUser?.role === 'leader' || currentUser?.role === 'admin';
  const canCreateUsers = currentUser?.role === 'leader';

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const requireAdminAccess = useCallback(async () => {
    if (!currentUser?.id) {
      throw new Error(t.auth.notAuthenticated);
    }
    await assertUserHasRole(currentUser.id, ['leader', 'admin']);
  }, [currentUser?.id]);

  useEffect(() => {
    setActiveTab(parseAdminTabFromUrl(searchParams.get('tab')));
  }, [searchParams]);

  // Deep-link: /admin?tab=2&caseId=…&reports=1 → open Vezi Rapoartele
  useEffect(() => {
    if (searchParams.get('reports') !== '1') return;
    const caseId = searchParams.get('caseId');
    if (!caseId) return;
    if (parseAdminTabFromUrl(searchParams.get('tab')) !== 1) return;

    if (casesLoading) return;

    const caseItem = allCases.find((c) => c.id === caseId);
    const next = new URLSearchParams(searchParams);
    next.delete('caseId');
    next.delete('reports');

    if (caseItem) {
      setSelectedCaseForSessionReport(caseItem);
      setSessionReportOpen(true);
    }

    setSearchParams(next, { replace: true });
  }, [searchParams, casesLoading, allCases, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('reassign') !== '1') return;
    if (searchParams.get('reports') === '1') return;
    const caseId = searchParams.get('caseId');
    if (!caseId) return;
    if (parseAdminTabFromUrl(searchParams.get('tab')) !== 1) return;
    if (casesLoading) return;

    const caseItem = allCases.find((c) => c.id === caseId);
    const next = new URLSearchParams(searchParams);
    next.delete('caseId');
    next.delete('reassign');

    if (caseItem) {
      setReassigningCase(caseItem);
    }

    setSearchParams(next, { replace: true });
  }, [searchParams, casesLoading, allCases, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('create') !== 'true') return;
    if (parseAdminTabFromUrl(searchParams.get('tab')) !== 1) return;

    setEditingCase(null);
    setCaseFormOpen(true);

    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const usersList = await getAllUsers();
      setUsers(usersList);
      usersLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading users:', error);
      showSnackbar(t.admin.users.createUserError, 'error');
    } finally {
      setUsersLoading(false);
    }
  }, [getAllUsers, showSnackbar]);

  const loadCounselors = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) {
        setCounselorsLoading(true);
      }
      setCounselorsError('');

      const counselorsSnapshot = await getDocs(
        query(collection(db, 'counselors'), orderBy('createdAt', 'desc'))
      );

      const counselorsData: Counselor[] = [];
      counselorsSnapshot.forEach((counselorDoc) => {
        counselorsData.push(
          mapFirestoreCounselor(counselorDoc.id, counselorDoc.data(), {
            activeCases: 0,
            workloadLevel: 'low',
          })
        );
      });

      setRawCounselors(dedupeCounselors(counselorsData));
      counselorsLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading counselors:', error);
      setCounselorsError(t.counselors.loadError);
    } finally {
      if (!silent) {
        setCounselorsLoading(false);
      }
    }
  }, []);

  const loadCases = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) {
        setCasesLoading(true);
      }
      setCasesError('');

      const casesSnapshot = await getDocs(
        query(collection(db, 'cases'), orderBy('createdAt', 'desc'))
      );

      const casesData: Case[] = [];
      casesSnapshot.forEach((caseDoc) => {
        casesData.push(mapFirestoreCase(caseDoc.id, caseDoc.data()));
      });

      setAllCases(casesData);
      casesLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading cases:', error);
      setCasesError('Eroare la încărcarea cazurilor');
    } finally {
      if (!silent) {
        setCasesLoading(false);
      }
    }
  }, []);

  /**
   * Loads what the active tab shows, and only what has not loaded yet.
   *
   * Users: tab 0 lists them, tab 4 picks mentors from them, and tab 1 needs them too — its
   * case form and reassign dialog take `inactiveUserIds` so a deactivated counselor cannot be
   * assigned. Only the leader-only tabs 2 and 3 skip the users scan. Cases and counselors
   * travel together on every tab that shows either, because counselor workload is derived from
   * the cases list. Once a collection is in, switching tabs does not fetch it again — the
   * mutation handlers refresh explicitly after each write.
   */
  useEffect(() => {
    const showsUsers = activeTab === 0 || activeTab === 1 || activeTab === 4;
    const showsCasesAndCounselors = activeTab === 0 || activeTab === 1 || activeTab === 4;

    if (showsUsers && currentUser && canManageUsers && !usersLoadedRef.current) {
      loadUsers();
    }
    if (showsCasesAndCounselors) {
      if (!casesLoadedRef.current) loadCases();
      if (!counselorsLoadedRef.current) loadCounselors();
    }
  }, [activeTab, currentUser, canManageUsers, loadUsers, loadCases, loadCounselors]);

  const filteredCounselors = useMemo(
    () => filterCounselors(counselors, counselorSearchTerm, counselorWorkloadFilter),
    [counselors, counselorSearchTerm, counselorWorkloadFilter]
  );

  const counselorWorkloadCounts = useMemo(
    () => ({
      all: counselors.length,
      low: countByWorkload(counselors, 'low'),
      moderate: countByWorkload(counselors, 'moderate'),
      high: countByWorkload(counselors, 'high'),
    }),
    [counselors]
  );

  const filteredUsers = useMemo(
    () => filterUsersByWorkload(users, counselors, userWorkloadFilter),
    [users, counselors, userWorkloadFilter]
  );

  const userWorkloadCounts = useMemo(
    () => countUsersByWorkload(users, counselors),
    [users, counselors]
  );

  const filteredCases = useMemo(
    () => filterAdminCases(allCases, caseSearchTerm, caseStatusFilter, caseCounselorFilter),
    [allCases, caseSearchTerm, caseStatusFilter, caseCounselorFilter]
  );

  const caseStatusCounts = useMemo(
    () => countAdminCasesByStatus(allCases, caseSearchTerm, caseCounselorFilter),
    [allCases, caseSearchTerm, caseCounselorFilter]
  );

  const setTab = useCallback(
    (tab: AdminTab) => {
      setActiveTab(tab);
      setSearchParams(adminTabToSearchParam(tab));
    },
    [setSearchParams]
  );

  // Tabs 3–4 (Rapoarte primite, Consimțăminte) are leader-only
  useEffect(() => {
    if (activeTab !== 2 && activeTab !== 3) return;
    if (currentUser?.role === 'leader') return;
    setTab(0);
  }, [activeTab, currentUser?.role, setTab]);

  const handleCounselorSubmit = useCallback(
    async (
      counselorData: Omit<Counselor, 'id' | 'createdAt' | 'updatedAt' | 'activeCases' | 'workloadLevel'>
    ) => {
      try {
        await requireAdminAccess();

        if (editingCounselor) {
          await updateDoc(doc(db, 'counselors', editingCounselor.id), {
            fullName: counselorData.fullName,
            firstName: counselorData.firstName ?? null,
            lastName: counselorData.lastName ?? null,
            email: counselorData.email,
            phoneNumber: counselorData.phoneNumber,
            sex: counselorData.sex ?? null,
            birthDate: counselorData.birthDate ?? null,
            specialties: counselorData.specialties,
            specialtyCategories: counselorData.specialtyCategories ?? null,
            linkedUserId: counselorData.linkedUserId ?? null,
            avatarUrl: counselorData.avatarUrl ?? null,
            updatedAt: new Date(),
          });
          await syncLinkedUserAvatar(counselorData.linkedUserId, counselorData.avatarUrl);
          showSnackbar('Consilier actualizat cu succes', 'success');
        } else {
          await addDoc(collection(db, 'counselors'), {
            fullName: counselorData.fullName,
            firstName: counselorData.firstName ?? null,
            lastName: counselorData.lastName ?? null,
            email: counselorData.email,
            phoneNumber: counselorData.phoneNumber,
            sex: counselorData.sex ?? null,
            birthDate: counselorData.birthDate ?? null,
            specialties: counselorData.specialties,
            specialtyCategories: counselorData.specialtyCategories ?? null,
            linkedUserId: counselorData.linkedUserId ?? null,
            avatarUrl: counselorData.avatarUrl ?? null,
            createdAt: new Date(),
            updatedAt: new Date(),
          });
          await syncLinkedUserAvatar(counselorData.linkedUserId, counselorData.avatarUrl);
          showSnackbar('Consilier creat cu succes', 'success');
        }
        setCounselorFormOpen(false);
        setEditingCounselor(null);
        setNewlyCreatedUserId(null);
        setPendingProfileRequired(false);
        await loadCounselors({ silent: true });
      } catch (error) {
        console.error('Error saving counselor:', error);
        const message =
          error instanceof Error && error.message === t.auth.permissionDenied
            ? error.message
            : t.counselors.saveError;
        showSnackbar(message, 'error');
      }
    },
    [editingCounselor, loadCounselors, requireAdminAccess, showSnackbar]
  );

  const handleDeleteCounselor = useCallback(
    async (counselorId: string) => {
      try {
        await requireAdminAccess();

        await deleteDoc(doc(db, 'counselors', counselorId));
        showSnackbar('Consilier șters cu succes', 'success');
        await loadCounselors({ silent: true });
      } catch (error) {
        console.error('Error deleting counselor:', error);
        const message =
          error instanceof Error && error.message === t.auth.permissionDenied
            ? error.message
            : t.counselors.deleteError;
        showSnackbar(message, 'error');
      }
    },
    [loadCounselors, requireAdminAccess, showSnackbar]
  );

  const handleCaseSubmit = useCallback(
    async (caseData: Omit<Case, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => {
      try {
        await requireAdminAccess();

        // An edit that leaves the proposal untouched must not reassign its authorship: this
        // field is the address the accept/decline notification is sent to.
        const proposalUnchanged =
          editingCase?.assignmentStatus === 'pending' &&
          caseData.proposedCounselorId === editingCase.proposedCounselorId;
        const priorProposerId = proposalUnchanged ? editingCase?.proposedByUserId : null;
        const priorProposerName = proposalUnchanged ? editingCase?.proposedByUserName : null;

        const firestorePayload = {
          ...caseData,
          assignedCounselorId: caseData.assignedCounselorId ?? null,
          assignedCounselorName: caseData.assignedCounselorName ?? null,
          proposedCounselorId: caseData.proposedCounselorId ?? null,
          proposedCounselorName: caseData.proposedCounselorName ?? null,
          assignmentStatus: caseData.assignmentStatus ?? 'none',
          proposedByUserId:
            caseData.assignmentStatus === 'pending'
              ? priorProposerId || currentUser?.id || null
              : null,
          proposedByUserName:
            caseData.assignmentStatus === 'pending'
              ? priorProposerName || currentUser?.fullName || currentUser?.email || null
              : null,
          referralSource: caseData.referralSource ?? null,
          priority: caseData.priority ?? 'normal',
          firstName: caseData.firstName ?? null,
          lastName: caseData.lastName ?? null,
          counseledName: caseData.counseledName,
          updatedAt: new Date(),
        };

        const notifyCounselor = async (
          caseId: string,
          caseTitle: string,
          counselorId: string | undefined,
          mode: 'proposed' | 'assigned'
        ) => {
          if (!counselorId || !currentUser) return;
          const counselor = counselors.find((c) => c.id === counselorId);
          const assignedToUserId = counselor?.linkedUserId || counselorId;
          const assignedToUserName = counselor?.fullName || 'Unknown Counselor';
          const byId = currentUser.id;
          const byName = currentUser.fullName || currentUser.email || 'Unknown User';
          if (mode === 'proposed') {
            await logCaseProposed(
              caseId,
              caseTitle,
              assignedToUserId,
              assignedToUserName,
              byId,
              byName
            );
          } else {
            await logCaseAssigned(
              caseId,
              caseTitle,
              assignedToUserId,
              assignedToUserName,
              byId,
              byName
            );
          }
        };

        if (editingCase) {
          const wasPending = editingCase.assignmentStatus === 'pending';
          const nowPending = caseData.assignmentStatus === 'pending';
          const nowForcedOrAssigned =
            caseData.assignmentStatus === 'forced' ||
            (caseData.assignmentStatus === 'accepted' && caseData.assignedCounselorId);
          const proposalChanged =
            nowPending &&
            caseData.proposedCounselorId &&
            caseData.proposedCounselorId !== editingCase.proposedCounselorId;
          const forceChanged =
            Boolean(caseData.assignedCounselorId) &&
            caseData.assignedCounselorId !== editingCase.assignedCounselorId &&
            (caseData.assignmentStatus === 'forced' ||
              (!wasPending && Boolean(caseData.assignedCounselorId)));

          await updateDoc(doc(db, 'cases', editingCase.id), firestorePayload);

          if (proposalChanged) {
            await notifyCounselor(
              editingCase.id,
              editingCase.title,
              caseData.proposedCounselorId || undefined,
              'proposed'
            );
          } else if (
            forceChanged ||
            (nowForcedOrAssigned &&
              caseData.assignedCounselorId &&
              caseData.assignedCounselorId !== editingCase.assignedCounselorId)
          ) {
            await notifyCounselor(
              editingCase.id,
              editingCase.title,
              caseData.assignedCounselorId,
              'assigned'
            );
          }

          showSnackbar(t.cases.updateSuccess, 'success');
          const updatedCase: Case = {
            ...editingCase,
            ...caseData,
            updatedAt: new Date(),
          };
          if (
            currentUser &&
            shouldAppearInPersonalCases(updatedCase, counselorRecordId, currentUser.id)
          ) {
            upsertCase(updatedCase);
          } else {
            removeCase(editingCase.id);
          }
        } else {
          const docRef = await addDoc(collection(db, 'cases'), {
            ...firestorePayload,
            createdBy: currentUser?.id || '',
            createdAt: new Date(),
          });

          if (currentUser) {
            await logCaseCreated(
              docRef.id,
              caseData.title,
              currentUser.id,
              currentUser.fullName || currentUser.email || 'Utilizator'
            );
          }

          if (caseData.assignmentStatus === 'pending' && caseData.proposedCounselorId) {
            await notifyCounselor(
              docRef.id,
              caseData.title,
              caseData.proposedCounselorId,
              'proposed'
            );
          } else if (caseData.assignedCounselorId) {
            await notifyCounselor(
              docRef.id,
              caseData.title,
              caseData.assignedCounselorId,
              'assigned'
            );
          }
          showSnackbar('Caz creat cu succes', 'success');
          const createdCase: Case = {
            ...caseData,
            id: docRef.id,
            createdBy: currentUser?.id || '',
            createdAt: new Date(),
            updatedAt: new Date(),
          } as Case;
          if (
            currentUser &&
            shouldAppearInPersonalCases(createdCase, counselorRecordId, currentUser.id)
          ) {
            upsertCase(createdCase);
          } else {
            removeCase(docRef.id);
          }
        }
        setCaseFormOpen(false);
        setEditingCase(null);
        await loadCases({ silent: true });
        await loadCounselors({ silent: true });
      } catch (error) {
        console.error('Error saving case:', error);
        const message =
          error instanceof Error && error.message === t.auth.permissionDenied
            ? error.message
            : t.cases.updateError;
        showSnackbar(message, 'error');
      }
    },
    [
      editingCase,
      counselors,
      currentUser,
      loadCases,
      loadCounselors,
      requireAdminAccess,
      showSnackbar,
      upsertCase,
      removeCase,
      counselorRecordId,
    ]
  );

  const handleReassignCase = useCallback(
    async (caseItem: Case, assignment: ReassignAssignmentFields) => {
      try {
        await requireAdminAccess();

        const nowPending = assignment.assignmentStatus === 'pending';
        const firestorePayload = {
          assignedCounselorId: assignment.assignedCounselorId ?? null,
          assignedCounselorName: assignment.assignedCounselorName ?? null,
          proposedCounselorId: assignment.proposedCounselorId ?? null,
          proposedCounselorName: assignment.proposedCounselorName ?? null,
          assignmentStatus: assignment.assignmentStatus ?? 'none',
          status: assignment.status,
          proposedByUserId: nowPending && currentUser ? currentUser.id : null,
          proposedByUserName:
            nowPending && currentUser
              ? currentUser.fullName || currentUser.email || null
              : null,
          updatedAt: new Date(),
        };

        await updateDoc(doc(db, 'cases', caseItem.id), firestorePayload);

        const counselorId =
          (nowPending ? assignment.proposedCounselorId : assignment.assignedCounselorId) ||
          undefined;
        if (counselorId && currentUser) {
          const counselor = counselors.find((c) => c.id === counselorId);
          const assignedToUserId = counselor?.linkedUserId || counselorId;
          const assignedToUserName = counselor?.fullName || 'Unknown Counselor';
          const byId = currentUser.id;
          const byName = currentUser.fullName || currentUser.email || 'Unknown User';
          if (nowPending) {
            await logCaseProposed(
              caseItem.id,
              caseItem.title,
              assignedToUserId,
              assignedToUserName,
              byId,
              byName
            );
          } else {
            await logCaseAssigned(
              caseItem.id,
              caseItem.title,
              assignedToUserId,
              assignedToUserName,
              byId,
              byName
            );
          }
        }

        const updatedCase: Case = {
          ...caseItem,
          ...assignment,
          proposedByUserId: firestorePayload.proposedByUserId,
          proposedByUserName: firestorePayload.proposedByUserName,
          updatedAt: new Date(),
        };
        if (
          currentUser &&
          shouldAppearInPersonalCases(updatedCase, counselorRecordId, currentUser.id)
        ) {
          upsertCase(updatedCase);
        } else {
          removeCase(caseItem.id);
        }

        await resolveAssignmentOutcomesForCase(caseItem.id);

        showSnackbar(t.cases.reassignSuccess, 'success');
        setReassigningCase(null);
        await loadCases({ silent: true });
        await loadCounselors({ silent: true });
      } catch (error) {
        console.error('Error reassigning case:', error);
        const message =
          error instanceof Error && error.message === t.auth.permissionDenied
            ? error.message
            : t.cases.updateError;
        showSnackbar(message, 'error');
      }
    },
    [
      counselors,
      currentUser,
      loadCases,
      loadCounselors,
      requireAdminAccess,
      showSnackbar,
      upsertCase,
      removeCase,
      counselorRecordId,
      resolveAssignmentOutcomesForCase,
    ]
  );

  const handleDeleteCase = useCallback(
    async (caseId: string) => {
      try {
        await requireAdminAccess();

        await deleteDoc(doc(db, 'cases', caseId));
        showSnackbar('Caz șters cu succes', 'success');
        removeCase(caseId);
        await clearAssignmentNotificationsForCase(caseId);
        await loadCases({ silent: true });
      } catch (error) {
        console.error('Error deleting case:', error);
        const message =
          error instanceof Error && error.message === t.auth.permissionDenied
            ? error.message
            : 'Eroare la ștergerea cazului';
        showSnackbar(message, 'error');
      }
    },
    [
      loadCases,
      requireAdminAccess,
      showSnackbar,
      removeCase,
      clearAssignmentNotificationsForCase,
    ]
  );


  const buildCounselorFields = useCallback((data: Partial<CreateUserData>, email: string, userId: string) => {
    return {
      fullName: (data.fullName || '').trim(),
      firstName: data.firstName?.trim() || null,
      lastName: data.lastName?.trim() || null,
      email,
      phoneNumber: toE164RoPhone(data.phoneNumber || ''),
      sex: data.sex || null,
      birthDate: parseDateInputValue(data.birthDate || ''),
      specialties: data.specialties ?? [],
      specialtyCategories:
        data.specialtyCategories && Object.keys(data.specialtyCategories).length > 0
          ? data.specialtyCategories
          : null,
      linkedUserId: userId,
    };
  }, []);

  const handleCreateUser = useCallback(async () => {
    if (
      !createUserData.firstName.trim() ||
      !createUserData.lastName.trim() ||
      !createUserData.password
    ) {
      showSnackbar(t.admin.users.createUserError, 'error');
      return;
    }
    if (!isValidEmail(createUserData.email)) {
      showSnackbar(t.admin.users.emailInvalid, 'error');
      return;
    }
    const email = createUserData.email.trim();
    const password = createUserData.password;
    const alreadyCopied = credentialsManuallyCopiedRef.current;
    try {
      setCreateUserLoading(true);
      const createdRole = createUserData.role;
      const newUserId = await createUser(
        email,
        password,
        createUserData.fullName.trim(),
        createdRole
      );
      requestAccountCredentialsEmail(newUserId, password);

      let autoCopied = false;
      if (!alreadyCopied) {
        try {
          await navigator.clipboard.writeText(
            `Email: ${email}\nPassword: ${password}\nLink app: https://consiliere.biserica-lumina.ro/login`
          );
          autoCopied = true;
        } catch {
          // Clipboard may be blocked; user can still copy manually next time
        }
      }

      showSnackbar(
        autoCopied
          ? t.admin.users.createUserSuccessCredentialsCopied
          : t.admin.users.createUserSuccess,
        'success'
      );
      credentialsManuallyCopiedRef.current = false;
      setCreateDialogOpen(false);

      const profile = buildCounselorFields(createUserData, email, newUserId);
      await addDoc(collection(db, 'counselors'), {
        ...profile,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      setCreateUserData(emptyCreateUserData());
      setNewlyCreatedUserId(null);
      setPendingProfileRequired(false);
      await loadUsers();
      await loadCounselors({ silent: true });
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      console.error('Error creating user:', error);
      if (err.code === 'auth/email-already-in-use') {
        showSnackbar(t.admin.users.emailAlreadyInUse, 'error');
      } else if (err.code === 'auth/invalid-email') {
        showSnackbar(t.admin.users.emailInvalid, 'error');
      } else {
        showSnackbar(t.admin.users.createUserError, 'error');
      }
    } finally {
      setCreateUserLoading(false);
    }
  }, [createUser, createUserData, loadUsers, loadCounselors, showSnackbar, buildCounselorFields]);

  const handleEditUser = useCallback(async () => {
    if (!selectedUser) return;
    try {
      setEditUserLoading(true);
      await requireAdminAccess();
      if (editUserData.role) {
        await updateUserRole(selectedUser.id, editUserData.role as UserRole);
      }
      const fullName = (editUserData.fullName || selectedUser.fullName).trim();
      await updateDoc(doc(db, 'users', selectedUser.id), {
        fullName,
        updatedAt: new Date(),
      });

      const profile = buildCounselorFields(
        editUserData,
        selectedUser.email,
        selectedUser.id
      );
      const existing = findCounselorForUser(counselors, selectedUser.id);
      if (existing) {
        await updateDoc(doc(db, 'counselors', existing.id), {
          ...profile,
          updatedAt: new Date(),
        });
      } else {
        await addDoc(collection(db, 'counselors'), {
          ...profile,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      }

      showSnackbar(t.admin.users.updateUserSuccess, 'success');
      setEditDialogOpen(false);
      setSelectedUser(null);
      await loadUsers();
      await loadCounselors({ silent: true });
    } catch (error) {
      console.error('Error updating user:', error);
      showSnackbar(t.admin.users.updateUserError, 'error');
    } finally {
      setEditUserLoading(false);
    }
  }, [
    selectedUser,
    editUserData,
    updateUserRole,
    loadUsers,
    loadCounselors,
    showSnackbar,
    requireAdminAccess,
    buildCounselorFields,
    counselors,
  ]);

  const handleDeleteUser = useCallback(
    async (userId: string) => {
      try {
        await deleteUser(userId);
        showSnackbar(t.admin.users.deleteUserSuccess, 'success');
        loadUsers();
        loadCounselors({ silent: true });
        loadCases({ silent: true });
      } catch (error) {
        console.error('Error deleting user:', error);
        showSnackbar(t.admin.users.deleteUserError, 'error');
      }
    },
    [deleteUser, loadUsers, loadCounselors, loadCases, showSnackbar]
  );

  const handleTransferSupremeLeader = useCallback(
    async (userId: string) => {
      try {
        await transferSupremeLeader(userId);
        showSnackbar(t.admin.users.transferSupremeLeaderSuccess, 'success');
        loadUsers();
      } catch (error) {
        console.error('Error transferring supreme leader:', error);
        showSnackbar(t.admin.users.transferSupremeLeaderError, 'error');
      }
    },
    [transferSupremeLeader, loadUsers, showSnackbar]
  );

  const handleDeactivateUser = useCallback(
    async (userId: string) => {
      try {
        await deactivateUser(userId);
        showSnackbar(t.admin.users.deactivateUserSuccess, 'success');
        loadUsers();
      } catch (error) {
        console.error('Error deactivating user:', error);
        showSnackbar(t.admin.users.deactivateUserError, 'error');
      }
    },
    [deactivateUser, loadUsers, showSnackbar]
  );

  const handleReactivateUser = useCallback(
    async (userId: string) => {
      try {
        setReactivatingUserId(userId);
        await reactivateUser(userId);
        showSnackbar(t.admin.users.reactivateUserSuccess, 'success');
        loadUsers();
      } catch (error) {
        console.error('Error reactivating user:', error);
        showSnackbar(t.admin.users.reactivateUserError, 'error');
      } finally {
        setReactivatingUserId(null);
      }
    },
    [reactivateUser, loadUsers, showSnackbar]
  );

  const openCreateUserDialog = useCallback(() => {
    credentialsManuallyCopiedRef.current = false;
    setCreateUserData(emptyCreateUserData());
    setCreateDialogOpen(true);
  }, []);

  const closeCreateUserDialog = useCallback(() => {
    if (createUserLoading) return;
    credentialsManuallyCopiedRef.current = false;
    setCreateDialogOpen(false);
  }, [createUserLoading]);

  const copyUserCredentials = useCallback(() => {
    const credentials = `Email: ${createUserData.email}
Password: ${createUserData.password}
Link app: https://consiliere.biserica-lumina.ro/login`;

    navigator.clipboard
      .writeText(credentials)
      .then(() => {
        credentialsManuallyCopiedRef.current = true;
        showSnackbar(t.admin.users.credentialsCopied, 'success');
      })
      .catch(() => showSnackbar(t.admin.users.createUserError, 'error'));
  }, [createUserData.email, createUserData.password, showSnackbar]);

  const openEditDialog = useCallback(
    (user: User) => {
      setSelectedUser(user);
      const counselor = findCounselorForUser(counselors, user.id);
      const { firstName, lastName } = splitDisplayName(user.fullName);
      setEditUserData({
        firstName: counselor?.firstName || firstName,
        lastName: counselor?.lastName || lastName,
        fullName: user.fullName,
        role: user.role,
        phoneNumber: toRoPhoneDigitsInput(counselor?.phoneNumber),
        sex: counselor?.sex || '',
        birthDate: toDateInputValue(counselor?.birthDate),
        specialties: counselor?.specialties || [],
        specialtyCategories: counselor?.specialtyCategories || {},
      });
      setEditDialogOpen(true);
    },
    [counselors]
  );

  const handleSkipCounselorProfile = useCallback(() => {
    if (pendingProfileRequired) return;
    setCounselorFormOpen(false);
    setNewlyCreatedUserId(null);
    setPendingProfileRequired(false);
    setEditingCounselor(null);
  }, [pendingProfileRequired]);

  const getCasesForCounselor = useCallback(
    (counselorId: string) => allCases.filter((c) => c.assignedCounselorId === counselorId),
    [allCases]
  );

  return {
    currentUser,
    canManageUsers,
    canCreateUsers,
    isSupremeLeader,
    activeTab,
    setTab,
    users,
    filteredUsers,
    usersLoading,
    snackbar,
    setSnackbar,
    showSnackbar,
    createDialogOpen,
    setCreateDialogOpen,
    openCreateUserDialog,
    closeCreateUserDialog,
    createUserLoading,
    editUserLoading,
    reactivatingUserId,
    editDialogOpen,
    setEditDialogOpen,
    selectedUser,
    createUserData,
    setCreateUserData,
    editUserData,
    setEditUserData,
    newlyCreatedUserId,
    pendingProfileRequired,
    counselors,
    counselorsLoading,
    counselorsError,
    counselorFormOpen,
    setCounselorFormOpen,
    editingCounselor,
    setEditingCounselor,
    counselorSearchTerm,
    setCounselorSearchTerm,
    counselorWorkloadFilter,
    setCounselorWorkloadFilter,
    userWorkloadFilter,
    setUserWorkloadFilter,
    userWorkloadCounts,
    filteredCounselors,
    counselorWorkloadCounts,
    allCases,
    casesLoading,
    casesError,
    caseSearchTerm,
    setCaseSearchTerm,
    caseStatusFilter,
    setCaseStatusFilter,
    caseCounselorFilter,
    setCaseCounselorFilter,
    filteredCases,
    caseStatusCounts,
    caseFormOpen,
    setCaseFormOpen,
    editingCase,
    setEditingCase,
    reassigningCase,
    setReassigningCase,
    sessionReportOpen,
    setSessionReportOpen,
    selectedCaseForSessionReport,
    setSelectedCaseForSessionReport,
    loadUsers,
    loadCases,
    handleCounselorSubmit,
    handleDeleteCounselor,
    handleCaseSubmit,
    handleReassignCase,
    handleDeleteCase,
    handleCreateUser,
    handleEditUser,
    handleDeleteUser,
    handleTransferSupremeLeader,
    handleDeactivateUser,
    handleReactivateUser,
    copyUserCredentials,
    openEditDialog,
    handleSkipCounselorProfile,
    handleCloseCounselorForm: () => {
      if (pendingProfileRequired && newlyCreatedUserId) return;
      setCounselorFormOpen(false);
      setEditingCounselor(null);
      setNewlyCreatedUserId(null);
      setPendingProfileRequired(false);
    },
    handleCloseCaseForm: () => {
      setCaseFormOpen(false);
      setEditingCase(null);
    },
    handleOpenSessionReport: (caseItem: Case) => {
      setSelectedCaseForSessionReport(caseItem);
      setSessionReportOpen(true);
    },
    handleCloseSessionReport: () => {
      setSessionReportOpen(false);
      setSelectedCaseForSessionReport(null);
    },
    getCasesForCounselor,
  };
}
